/**
 * The low end: the chromatic 808 set and the playable bass patches.
 *
 * The 808s are the reason this file exists. A modern beat's bass line *is* its 808 — played
 * melodically across the pads, one note per pad, sliding from one to the next — so a kit that
 * ships a single "808" sample ships a kick, not an instrument. What is rendered here is every
 * semitone from C1 to C3: two full octaves starting at 32.7 Hz, named by note, tuned by equal
 * temperament rather than by resampling, so the top of the range is as clean as the bottom.
 */

import { clamp } from '../../model/types';
import { makeSynthEnvelope, makeSynthFilter, makeSynthLayer, makeSynthLFO } from '../synth';
import { libraryLayer, librarySound, type LibrarySound } from './model';
import { noteFrequency, noteName, noteSlug } from './notes';
import { bodyPatch, effect, libraryChain, libraryPatch, transientPatch } from './patch';

/** C1 (MIDI 24, 32.70 Hz) to C3 (MIDI 48, 130.81 Hz), every semitone. */
export const LOWEST_808 = 24;
export const HIGHEST_808 = 48;

/**
 * One 808 note.
 *
 * Three things make this an 808 rather than a sine: the short drop into pitch at the start,
 * which is the tone's transient and the reason it reads as a struck note; the saturation,
 * which puts harmonics above the fundamental so the note is still audible on a speaker with
 * no output below 200 Hz; and the click layer, which gives the ear something to place in time
 * when everything else is a sub.
 */
function eightOhEight(midi: number): LibrarySound {
  const note = noteName(midi);
  const slug = `808-${noteSlug(midi)}`;
  const frequency = noteFrequency(midi);

  // Higher notes get shorter tails. A C3 held as long as a C1 stops being a bass note and
  // starts fighting whatever is playing over it.
  const position = (midi - LOWEST_808) / (HIGHEST_808 - LOWEST_808);
  const decay = 1.55 - 0.6 * position;

  return librarySound(
    slug,
    `808 ${note}`,
    '808',
    `808 bass at ${note}. A clicked attack over a long, saturated low tone.`,
    [
      libraryLayer(
        bodyPatch({
          slug,
          slot: 0,
          frequency,
          dropSemitones: 10,
          dropSeconds: 0.028,
          dropCurve: 0.85,
          decay,
          hold: 0.012,
          release: 0.06,
          curve: 0.4,
          // Opened by the envelope for the first 40 ms so the attack has some brightness,
          // then parked low enough to keep the sustain a sub.
          lowPass: clamp(frequency * 12, 380, 1600),
          lowPassOctaves: 2.2,
          lowPassDecay: 0.04,
          drive: 0.32,
        }),
      ),
      libraryLayer(transientPatch({ slug, slot: 1, highPass: 2400, decay: 0.004 }), { gainDB: -17 }),
    ],
    { tags: ['808', 'bass', 'sub', 'trap', note.toLowerCase()] },
  );
}

export const eightOhEights: LibrarySound[] = Array.from(
  { length: HIGHEST_808 - LOWEST_808 + 1 },
  (_, index) => eightOhEight(LOWEST_808 + index),
);

/**
 * The pitches every bass timbre is rendered at. Three notes an octave apart in the bottom of
 * the range covers what a pad grid is actually played with; anything between them is a
 * semitone of pitch shift away in the pad inspector.
 */
const BASS_NOTES = [28, 33, 36]; // E1, A1, C2

interface BassIdentity {
  slug: string;
  note: string;
  frequency: number;
}

function identity(family: string, midi: number): BassIdentity {
  return { slug: `${family}-${noteSlug(midi)}`, note: noteName(midi), frequency: noteFrequency(midi) };
}

/**
 * Nothing but a sine and a little saturation. The reference low end — if a mix works with
 * this under it, the problem is never the bass.
 */
function subBass(midi: number): LibrarySound {
  const { slug, note, frequency } = identity('bass-sub', midi);
  return librarySound(
    slug,
    `Sub Bass ${note}`,
    'bass',
    `Sub bass at ${note}. A pure low sine with a soft edge of saturation.`,
    [
      libraryLayer(
        bodyPatch({
          slug,
          slot: 0,
          frequency,
          dropSemitones: 2,
          dropSeconds: 0.02,
          decay: 0.9,
          hold: 0.01,
          attack: 0.004,
          release: 0.08,
          curve: 0.4,
          drive: 0.15,
        }),
      ),
    ],
    { tags: ['bass', 'sub', 'sine', note.toLowerCase()] },
  );
}

/**
 * Three detuned saws through a resonant low-pass — the sound a synth bass is expected to
 * make. Key tracking is deliberately left at zero: below middle C it *closes* the filter, and
 * a bass patch whose harmonics have been removed disappears on a phone.
 */
function sawBass(midi: number): LibrarySound {
  const { slug, note, frequency } = identity('bass-saw', midi);
  return librarySound(
    slug,
    `Saw Bass ${note}`,
    'bass',
    `Saw bass at ${note}. Bright and full, opening as the note starts.`,
    [
      libraryLayer(
        libraryPatch({
          slug,
          slot: 0,
          layers: [
            makeSynthLayer({ waveform: 'saw', level: 1.0, unisonVoices: 3, unisonDetuneCents: 12 }),
            makeSynthLayer({ waveform: 'sine', semitones: -12, level: 0.45 }),
          ],
          amplitude: makeSynthEnvelope({
            attack: 0.006,
            hold: 0.01,
            decay: 0.3,
            sustain: 0.5,
            release: 0.3,
            curve: 0.35,
          }),
          filter: makeSynthFilter({
            type: 'lowPass',
            cutoff: 800,
            resonance: 1.8,
            envelopeAmount: 1.6,
            envelope: makeSynthEnvelope({ attack: 0.004, decay: 0.25, sustain: 0.25, release: 0.2, curve: 0.6 }),
          }),
          baseFrequency: frequency,
          duration: 1.1,
          drive: 0.25,
        }),
      ),
    ],
    { tags: ['bass', 'saw', 'synth', note.toLowerCase()] },
  );
}

function squareBass(midi: number): LibrarySound {
  const { slug, note, frequency } = identity('bass-square', midi);
  return librarySound(
    slug,
    `Square Bass ${note}`,
    'bass',
    `Square bass at ${note}. Hollow and woody, like an old game console.`,
    [
      libraryLayer(
        libraryPatch({
          slug,
          slot: 0,
          layers: [
            makeSynthLayer({ waveform: 'square', level: 1.0 }),
            makeSynthLayer({ waveform: 'square', semitones: 12, level: 0.25 }),
            makeSynthLayer({ waveform: 'sine', semitones: -12, level: 0.4 }),
          ],
          amplitude: makeSynthEnvelope({
            attack: 0.005,
            hold: 0.01,
            decay: 0.28,
            sustain: 0.48,
            release: 0.28,
            curve: 0.35,
          }),
          filter: makeSynthFilter({
            type: 'lowPass',
            cutoff: 900,
            resonance: 1.5,
            envelopeAmount: 1.2,
            envelope: makeSynthEnvelope({ attack: 0.003, decay: 0.2, sustain: 0.2, release: 0.2, curve: 0.6 }),
          }),
          baseFrequency: frequency,
          duration: 1.1,
          drive: 0.2,
        }),
      ),
    ],
    { tags: ['bass', 'square', 'synth', note.toLowerCase()] },
  );
}

/**
 * A reese is *interference*, not a waveform: five saws detuned far enough apart that their
 * beating becomes the timbre, then a slow filter sweep over the top of it. The chorus widens
 * what is otherwise a very mono sound.
 */
function reeseBass(midi: number): LibrarySound {
  const { slug, note, frequency } = identity('bass-reese', midi);
  return librarySound(
    slug,
    `Reese Bass ${note}`,
    'bass',
    `Reese bass at ${note}. Detuned saws grinding against each other.`,
    [
      libraryLayer(
        libraryPatch({
          slug,
          slot: 0,
          layers: [
            makeSynthLayer({ waveform: 'saw', level: 1.0, unisonVoices: 5, unisonDetuneCents: 28 }),
            makeSynthLayer({
              waveform: 'saw',
              semitones: -12,
              level: 0.55,
              unisonVoices: 2,
              unisonDetuneCents: 14,
            }),
            makeSynthLayer({ waveform: 'sine', semitones: -12, level: 0.35 }),
          ],
          amplitude: makeSynthEnvelope({
            attack: 0.012,
            hold: 0.01,
            decay: 0.4,
            sustain: 0.7,
            release: 0.4,
            curve: 0.25,
          }),
          filter: makeSynthFilter({
            type: 'lowPass',
            cutoff: 600,
            resonance: 3.0,
            envelopeAmount: 1.0,
            envelope: makeSynthEnvelope({ attack: 0.02, decay: 0.6, sustain: 0.4, release: 0.4, curve: 0.3 }),
          }),
          lfo: makeSynthLFO({ isEnabled: true, waveform: 'triangle', rateHz: 1.6, depth: 0.3, target: 'filter' }),
          baseFrequency: frequency,
          duration: 1.6,
          drive: 0.3,
          chain: [effect('chorus', { rate: 0.35, depth: 0.35, voices: 2, mix: 0.35 })],
        }),
      ),
    ],
    { tags: ['bass', 'reese', 'detuned', 'dnb', note.toLowerCase()] },
  );
}

function pluckBass(midi: number): LibrarySound {
  const { slug, note, frequency } = identity('bass-pluck', midi);
  return librarySound(
    slug,
    `Pluck Bass ${note}`,
    'bass',
    `Plucked bass at ${note}. Short and snappy, gone before the next beat.`,
    [
      libraryLayer(
        libraryPatch({
          slug,
          slot: 0,
          layers: [
            makeSynthLayer({ waveform: 'saw', level: 0.8 }),
            makeSynthLayer({ waveform: 'square', semitones: -12, level: 0.6 }),
            makeSynthLayer({ waveform: 'sine', semitones: -12, level: 0.5 }),
          ],
          amplitude: makeSynthEnvelope({
            attack: 0.002,
            hold: 0.004,
            decay: 0.3,
            sustain: 0,
            release: 0.1,
            curve: 0.7,
          }),
          // The filter closing faster than the amplitude is the whole pluck: the note gets
          // darker as it dies, which is what a struck string does.
          filter: makeSynthFilter({
            type: 'lowPass',
            cutoff: 1400,
            resonance: 2.6,
            envelopeAmount: 2.4,
            envelope: makeSynthEnvelope({ attack: 0.002, decay: 0.09, sustain: 0, release: 0.05, curve: 0.8 }),
          }),
          baseFrequency: frequency,
          drive: 0.45,
        }),
      ),
    ],
    {
      tags: ['bass', 'pluck', 'short', note.toLowerCase()],
      // A pluck is one transient with very little behind it — measured at 15 dB of crest
      // factor, which meant matching it to the other basses took a boost big enough to clip
      // the attack flat. Limiting it first raises the body instead of the tip, and a limited
      // bass pluck is what a record has on it anyway.
      post: libraryChain(slug, [effect('limiter', { ceiling: -1.0, release: 0.05, drive: 6 })]),
    },
  );
}

/**
 * Ring modulation stands in for FM here, and it is not a compromise: a two-operator FM pair
 * at a low index produces sidebands at the carrier plus and minus the modulator, which is
 * exactly what a ring modulator produces. The inharmonic partial at 2.5× is where the
 * metallic, slightly bell-like edge of an FM bass comes from.
 */
function fmBass(midi: number): LibrarySound {
  const { slug, note, frequency } = identity('bass-fm', midi);
  return librarySound(
    slug,
    `FM Bass ${note}`,
    'bass',
    `FM bass at ${note}. A metallic, bell-edged low note.`,
    [
      libraryLayer(
        libraryPatch({
          slug,
          slot: 0,
          layers: [
            makeSynthLayer({ waveform: 'sine', level: 1.0 }),
            makeSynthLayer({ waveform: 'triangle', semitones: 12, level: 0.3 }),
          ],
          amplitude: makeSynthEnvelope({
            attack: 0.003,
            hold: 0.008,
            decay: 0.35,
            sustain: 0.35,
            release: 0.25,
            curve: 0.5,
          }),
          filter: makeSynthFilter({ type: 'lowPass', cutoff: 1600, resonance: 1.0 }),
          baseFrequency: frequency,
          duration: 1.0,
          drive: 0.25,
          chain: [
            effect('ringMod', { frequency: frequency * 2.5, mix: 0.45 }),
            effect('lowPass', { frequency: 2400, resonance: 0 }),
          ],
        }),
      ),
    ],
    { tags: ['bass', 'fm', 'metallic', note.toLowerCase()] },
  );
}

export const basses: LibrarySound[] = [
  ...BASS_NOTES.map(subBass),
  ...BASS_NOTES.map(sawBass),
  ...BASS_NOTES.map(squareBass),
  ...BASS_NOTES.map(reeseBass),
  ...BASS_NOTES.map(pluckBass),
  ...BASS_NOTES.map(fmBass),
];
